import { useEffect, useState } from 'react';
import { Wallet, Plus, Copy, Check, ExternalLink } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { useWalletStore } from '@/stores/walletStore';
import api from '@/lib/api';
import { Chain, CHAIN_CONFIGS } from '@hirenfire/shared';
import type { ApiResponse, WalletBalance } from '@hirenfire/shared';

export function WalletPage() {
  const { wallets, setWallets, selectedChain, setSelectedChain } = useWalletStore();
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addMode, setAddMode] = useState<'generate' | 'connect'>('generate');
  const [connectAddress, setConnectAddress] = useState('');
  const [addChain, setAddChain] = useState<Chain>(Chain.POLYGON);
  const [isAdding, setIsAdding] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);

  useEffect(() => {
    async function fetchWallets() {
      try {
        const res = await api.get<ApiResponse<WalletBalance[]>>('/wallets');
        if (res.data.data) setWallets(res.data.data);
      } catch {
        // fallback
      } finally {
        setIsLoading(false);
      }
    }
    fetchWallets();
  }, [setWallets]);

  const handleCopy = async (address: string) => {
    await navigator.clipboard.writeText(address);
    setCopiedAddress(address);
    setTimeout(() => setCopiedAddress(null), 2000);
  };

  const handleAddWallet = async () => {
    setIsAdding(true);
    try {
      if (addMode === 'generate') {
        await api.post('/wallets/generate', { chain: addChain });
      } else {
        await api.post('/wallets/connect', { chain: addChain, address: connectAddress });
      }
      const res = await api.get<ApiResponse<WalletBalance[]>>('/wallets');
      if (res.data.data) setWallets(res.data.data);
      setShowAddModal(false);
      setConnectAddress('');
    } catch {
      // handle error
    } finally {
      setIsAdding(false);
    }
  };

  const filteredWallets = wallets.filter(
    (w) => w.wallet.chain === selectedChain
  );

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Wallet</h1>
          <p className="text-gray-400 mt-1">Manage your crypto wallets</p>
        </div>
        <Button
          leftIcon={<Plus className="w-4 h-4" />}
          onClick={() => setShowAddModal(true)}
        >
          Add Wallet
        </Button>
      </div>

      <div className="flex gap-2">
        {Object.values(Chain).map((chain) => (
          <Button
            key={chain}
            variant={selectedChain === chain ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setSelectedChain(chain)}
          >
            {CHAIN_CONFIGS[chain].name}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Spinner size="lg" />
        </div>
      ) : filteredWallets.length === 0 ? (
        <Card>
          <CardBody className="py-16 text-center">
            <Wallet className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">
              No wallets on {CHAIN_CONFIGS[selectedChain].name}
            </p>
            <p className="text-gray-600 text-sm mt-2">
              Generate or connect a wallet to get started
            </p>
          </CardBody>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredWallets.map((wb) => (
            <Card key={wb.wallet.id}>
              <CardHeader
                action={
                  <div className="flex items-center gap-2">
                    {wb.wallet.isCustodial && (
                      <Badge variant="purple">Custodial</Badge>
                    )}
                    {wb.wallet.isDefault && (
                      <Badge variant="primary">Default</Badge>
                    )}
                  </div>
                }
              >
                <div className="flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-primary-400" />
                  {CHAIN_CONFIGS[wb.wallet.chain].name} Wallet
                </div>
              </CardHeader>
              <CardBody className="space-y-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Address</p>
                  <div className="flex items-center gap-2">
                    <code className="text-xs text-gray-400 font-mono bg-dark-700 px-2 py-1 rounded truncate flex-1">
                      {wb.wallet.address}
                    </code>
                    <button
                      onClick={() => handleCopy(wb.wallet.address)}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      {copiedAddress === wb.wallet.address ? (
                        <Check className="w-4 h-4 text-success-400" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                    <a
                      href={`${CHAIN_CONFIGS[wb.wallet.chain].explorerUrl}/address/${wb.wallet.address}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-gray-400 hover:text-primary-400 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-500 mb-2">Balances</p>
                  <div className="space-y-2">
                    {Object.entries(wb.balances).map(([currency, balance]) => (
                      <div
                        key={currency}
                        className="flex items-center justify-between p-2 bg-dark-700/50 rounded-lg"
                      >
                        <span className="text-sm text-gray-300">{currency}</span>
                        <span className="text-sm font-semibold text-white">
                          ${Number(balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Wallet"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setAddMode('generate')}
              className={`p-4 rounded-lg border text-center transition-all ${
                addMode === 'generate'
                  ? 'border-primary-500 bg-primary-500/10 text-primary-400'
                  : 'border-dark-600 bg-dark-800 text-gray-400 hover:border-dark-500'
              }`}
            >
              <p className="font-semibold">Generate</p>
              <p className="text-xs mt-1 opacity-70">Custodial wallet</p>
            </button>
            <button
              onClick={() => setAddMode('connect')}
              className={`p-4 rounded-lg border text-center transition-all ${
                addMode === 'connect'
                  ? 'border-accent-500 bg-accent-500/10 text-accent-400'
                  : 'border-dark-600 bg-dark-800 text-gray-400 hover:border-dark-500'
              }`}
            >
              <p className="font-semibold">Connect</p>
              <p className="text-xs mt-1 opacity-70">External wallet</p>
            </button>
          </div>

          <Select
            label="Blockchain"
            value={addChain}
            onChange={(e) => setAddChain(e.target.value as Chain)}
            options={Object.values(Chain).map((c) => ({
              value: c,
              label: CHAIN_CONFIGS[c].name,
            }))}
          />

          {addMode === 'connect' && (
            <Input
              label="Wallet Address"
              placeholder="0x... or SOL address"
              value={connectAddress}
              onChange={(e) => setConnectAddress(e.target.value)}
            />
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddWallet} isLoading={isAdding}>
              {addMode === 'generate' ? 'Generate Wallet' : 'Connect Wallet'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
